import {
  Card,
  Table as MuiTable,
  SxProps,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Theme,
  styled,
  tableCellClasses,
} from '@mui/material';
import React, { useState } from 'react';

import TablePaginationActions from './TablePaginationActions';

interface TableProps {
  disablePagination?: boolean;
  columns: string[];
  emptyText?: string;
  rows: any[];
  sx?: SxProps<Theme>;
  renderRow: (row: any, index: number) => JSX.Element;
  rowsPerPageOptions?: (number | { label: string; value: number })[];
  initialPage?: number;
  initialRowsPerPage?: number;
  headerAlignments?: { [key: string]: 'left' | 'center' | 'right' };
}

const Table: React.FC<TableProps> = ({
  disablePagination = false,
  emptyText = 'No results found',
  columns,
  rows,
  sx,
  renderRow,
  rowsPerPageOptions = [10, 25, { label: 'All', value: -1 }],
  initialPage = 0,
  initialRowsPerPage = 10,
  headerAlignments = {},
}) => {
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(
    disablePagination ? rows.length : initialRowsPerPage
  );

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const tableData =
    rowsPerPage > 0
      ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : rows;

  const emptyRows =
    rows.length === 0
      ? 1
      : page > 0
      ? Math.max(0, (1 + page) * rowsPerPage - rows.length)
      : 0;

  const shouldDisplayPagination = rows.length > rowsPerPage;

  return (
    <Card>
      <StyledTable sx={sx} size="small">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column}
                align={headerAlignments[column] || 'left'}
              >
                {column}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {tableData.map(renderRow)}
          {emptyRows > 0 && (
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ textAlign: 'center' }}>
                {rows.length === 0 && emptyText}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        {!disablePagination && shouldDisplayPagination && (
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={rowsPerPageOptions}
                colSpan={columns.length}
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                SelectProps={{ native: true }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        )}
      </StyledTable>
    </Card>
  );
};

const StyledTable = styled(MuiTable)(({ theme }) => ({
  '& .MuiTableRow-root:last-child td, & .MuiTableRow-root:last-child th': {
    borderBottom: 0,
  },
  '& .MuiTablePagination-toolbar': {
    padding: 1,
  },
  '& .MuiTableCell-footer': {
    padding: `0 ${theme.spacing(1)}`,
    borderTop: '1px solid',
    borderColor: theme.palette.divider,
  },
  '& .MuiTableCell-footer .MuiTablePagination-select': {
    padding: '0 24px 0 0',
  },
  [`& .${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.common.black,
  },
}));

export default Table;
